import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Globe, 
  Code, 
  Smartphone, 
  Palette,
  Shield,
  Zap,
  CheckCircle,
  MessageCircle,
  Settings
} from 'lucide-react';
import '../styles/CustomDevelopmentPage.css';

const CustomDevelopmentPage = () => {
  const reduceMotion = useReducedMotion();
  const [isCompact, setIsCompact] = useState(false);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const [servicesRef, servicesInView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const sync = () => setIsCompact(media.matches);
    sync();
    media.addEventListener('change', sync);
    return () => media.removeEventListener('change', sync);
  }, []);

  // Entra desde la izquierda → choca → 3 rebotes → cae al centro.
  const gearCrash = reduceMotion
    ? { x: 0, y: 0, scale: 1, rotate: 0 }
    : {
        x: isCompact
          ? [
              '-120vw', // salida
              '64vw',   // choque
              '10vw',   // rebote 1
              '42vw',
              '4vw',    // rebote 2
              '24vw',
              '1vw',    // rebote 3
              '10vw',
              '0vw'     // centro
            ]
          : [
              '-110vw',
              '34vw', // choque
              '4vw',  // rebote 1
              '22vw',
              '1vw',  // rebote 2
              '12vw',
              '2vw',  // rebote 3
              '6vw',
              '0vw'   // centro
            ],
        y: isCompact
          ? [0, -8, 24, -6, 16, -3, 10, -1, 0]
          : [0, -16, 30, -10, 20, -5, 12, -2, 0],
        scale: [1, 1.14, 0.82, 1.06, 0.88, 1.04, 0.93, 1.02, 1],
        rotate: [0, 620, 700, 780, 850, 920, 980, 1040, 1080]
      };

  const gearCrashTransition = reduceMotion
    ? { duration: 0 }
    : {
        duration: 4.2,
        times: [0, 0.3, 0.42, 0.52, 0.62, 0.72, 0.82, 0.91, 1],
        ease: [
          'easeIn',
          [0.15, 0.9, 0.25, 1],
          [0.34, 1.5, 0.64, 1],
          [0.22, 1, 0.36, 1],
          [0.34, 1.4, 0.64, 1],
          [0.22, 1, 0.36, 1],
          [0.34, 1.3, 0.64, 1],
          'easeOut'
        ],
        delay: 0.15
      };

  const services = [
    {
      id: 'websites',
      icon: Globe,
      title: 'Páginas Web',
      subtitle: 'Presencia digital profesional',
      description: 'Creamos páginas web modernas, totalmente responsivas y optimizadas para destacar tu marca. Diseñadas para desktop, tablet y móviles, mostrando tus productos o servicios de manera profesional.',
      price: 'Desde 200',
      currency: 'USD',
      features: [
        'Diseño responsivo (Desktop, Tablet, Móvil)',
        'Optimización SEO incluida',
        'Carga ultra rápida',
        'Diseño personalizado de tu marca',
        'Formularios de contacto',
        'Integración con redes sociales',
        'Codigo Fuente',
        'Hosting y Estrategia'
      ],
      benefits: [
        'Aumenta tu credibilidad profesional',
        'Atrae más clientes potenciales',
        'Disponible 24/7 para tus clientes',
        'Mejora tu posicionamiento en Google'
      ],
      whatsappMessage: 'Me gustaría cotizar un sitio web a medida',
      gradient: 'linear-gradient(135deg, #f9a550 0%, #ff8c42 100%)'
    },
    {
      id: 'applications',
      icon: Code,
      title: 'Aplicaciones a Medida',
      subtitle: 'Software personalizado para tu negocio',
      description: 'Desarrollamos software creado específicamente para las necesidades de tu negocio. Desde tiendas online hasta sistemas de gestión complejos, automatizamos tus procesos y optimizamos tu operación.',
      price: 'Desde 1000',
      currency: 'USD',
      features: [
        'Desarrollo personalizado 100%',
        'Tiendas online (E-commerce)',
        'Sistemas de presupuestación',
        'Autenticación de usuarios',
        'Sistemas de suscripciones',
        'Control de inventario y stock',
        'Dashboards y reportes',
        'APIs e integraciones',
        'Base de datos escalable',
        'Soporte técnico continuo'
      ],
      benefits: [
        'Automatiza procesos repetitivos',
        'Mejora la eficiencia operativa',
        'Escala con tu crecimiento',
        'Integra todos tus sistemas'
      ],
      whatsappMessage: 'Me gustaría cotizar una aplicación a medida',
      gradient: 'linear-gradient(135deg, #f9a550 0%, #ff8c42 100%)'
    }
  ];

  const whyChooseUs = [
    {
      icon: Palette,
      title: 'Diseño Profesional',
      description: 'Cada proyecto refleja la identidad única de tu marca con diseños modernos y atractivos.'
    },
    {
      icon: Zap,
      title: 'Tecnología Avanzada',
      description: 'Utilizamos las últimas tecnologías para garantizar rendimiento y escalabilidad.'
    },
    {
      icon: Shield,
      title: 'Soporte Continuo',
      description: 'Te acompañamos después del lanzamiento con soporte técnico y actualizaciones.'
    },
    {
      icon: Smartphone,
      title: '100% Responsive',
      description: 'Todas nuestras soluciones funcionan perfectamente en cualquier dispositivo.'
    }
  ];

  const generateWhatsAppLink = (message) => {
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/5491123428342?text=${encodedMessage}`;
  };

  return (
    <div className="custom-development-page">
      {/* Hero */}
      <section className="cd-hero" ref={ref}>
        <div className="cd-hero__glow" aria-hidden="true" />
        <div className="cd-hero__inner">
          <motion.div
            className="cd-hero__copy"
            initial={{ opacity: 0, y: 28 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, ease: 'easeOut' }}
          >
            <p className="cd-hero__eyebrow">Software empresarial</p>
            <h1 className="cd-hero__title">
              Desarrollos a Medida de sus{' '}
              <span className="cd-hero__title-accent">Necesidades</span>
            </h1>
            <p className="cd-hero__desc">
              Transformamos tus ideas en soluciones digitales poderosas que{' '}
              <strong>impulsan el crecimiento real</strong> de tu negocio.
            </p>

            <div className="cd-hero__pills">
              <div className="cd-hero__pill">
                <Globe size={18} strokeWidth={2.2} />
                <span>Páginas Web Profesionales</span>
              </div>
              <div className="cd-hero__pill">
                <Code size={18} strokeWidth={2.2} />
                <span>Aplicaciones Escalables</span>
              </div>
            </div>

            <p className="cd-hero__promise">
              <strong>Tecnología de vanguardia</strong>
              <span aria-hidden="true"> · </span>
              <strong>Diseño profesional</strong>
              <span aria-hidden="true"> · </span>
              <strong>Resultados garantizados</strong>
            </p>
          </motion.div>

          <div className="cd-hero__visual" aria-hidden="true">
            <div className="cd-hero__gear-ring">
              <motion.div
                className="cd-hero__gear-crash"
                initial={reduceMotion ? false : { x: '-110vw', y: 0, scale: 1, rotate: 0 }}
                animate={inView ? gearCrash : reduceMotion ? gearCrash : { x: '-110vw' }}
                transition={gearCrashTransition}
              >
                <motion.div
                  className="cd-hero__gear"
                  animate={
                    reduceMotion
                      ? undefined
                      : inView
                        ? { rotate: 360 }
                        : undefined
                  }
                  transition={
                    reduceMotion
                      ? undefined
                      : {
                          duration: 12,
                          repeat: Infinity,
                          ease: 'linear',
                          delay: 4.4
                        }
                  }
                >
                  <Settings className="cd-hero__gear-icon" strokeWidth={1.25} />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section" ref={servicesRef}>
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2>Nuestros <span className="text-gradient">Servicios</span></h2>
            <p>Soluciones digitales profesionales diseñadas para hacer crecer tu negocio</p>
          </motion.div>

          <div className="services-grid">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                className="service-card"
                initial={{ opacity: 0, y: 30 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <div className="service-header">
                  <div 
                    className="service-icon"
                    style={{ background: service.gradient }}
                  >
                    <service.icon size={28} color="white" />
                  </div>
                  <div className="service-title-section">
                    <h3>{service.title}</h3>
                    <p className="service-subtitle">{service.subtitle}</p>
                  </div>
                </div>

                <div className="service-description">
                  <p>{service.description}</p>
                </div>

                <div className="service-pricing">
                  <div className="price-container">
                    <span className="price-label">Inversión</span>
                    <div className="price-display">
                      <span className="price">{service.price}</span>
                      <span className="currency">{service.currency}</span>
                    </div>
                  </div>
                </div>

                <div className="service-features">
                  <h4>Incluye:</h4>
                  <ul>
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex}>
                        <span className="service-list-icon" aria-hidden="true">
                          <CheckCircle size={16} strokeWidth={2.2} />
                        </span>
                        <span className="service-list-text">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="service-benefits">
                  <h4>Beneficios:</h4>
                  <ul>
                    {service.benefits.map((benefit, benefitIndex) => (
                      <li key={benefitIndex}>
                        <span className="service-list-icon" aria-hidden="true">
                          <Zap size={16} strokeWidth={2.2} />
                        </span>
                        <span className="service-list-text">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="service-action">
                  <motion.a
                    href={generateWhatsAppLink(service.whatsappMessage)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="quote-btn"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <MessageCircle size={18} />
                    Cotizar
                  </motion.a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-us-section">
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h2>¿Por qué elegirnos?</h2>
            <p>Combinamos experiencia técnica con creatividad para entregar resultados excepcionales</p>
          </motion.div>

          <div className="features-grid">
            {whyChooseUs.map((feature, index) => (
              <motion.div
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.6 + (index * 0.1) }}
              >
                <div className="feature-icon">
                  <feature.icon size={24} color="#ffffff" strokeWidth={2.2} />
                </div>
                <h4>{feature.title}</h4>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <h2>¿Listo para transformar tu negocio?</h2>
            <p>
              Contáctanos hoy mismo y descubre cómo podemos ayudarte a crear la solución digital perfecta para tu empresa.
            </p>
            <motion.a
              href={generateWhatsAppLink('Hola! Me interesa conocer más sobre sus servicios de desarrollo a medida')}
              target="_blank"
              rel="noopener noreferrer"
              className="btn cta-btn"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <MessageCircle size={20} />
              Conversemos por WhatsApp
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CustomDevelopmentPage; 